import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Drawer,
  Divider,
  Grid,
  IconButton,
  Popper,
  Grow,
  Paper,
  MenuList,
  MenuItem,
  Typography,
  Avatar,
  Tooltip,
  Chip,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
} from '@material-ui/core';
import { Language } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { fetchGet, fetchPut } from '../../../helpers/fetching';
import { setSnackbar, setLanguage } from '../../../store';
import ChangePassword from './ChangePassword';
import CloseIcon from '@material-ui/icons/Close';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import LanguageIcon from '@material-ui/icons/Language';

const useStyles = makeStyles((theme) => ({
  drawerAvatarPaper: {
    width: '400px',
    overflow: 'auto',
  },

  drawerHeader: {
    display: 'grid',
    margin: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px',
    minHeight: '6% !important',
  },

  avatar_container: {
    display: 'flex',
    alignItems: 'center',
    gridColumn: '2',
    justifyContent: 'flex-start',
  },

  drawerContainer: {
    height: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
  },

  profile_avatar: {
    color: theme.palette.accents.main,
    height: '30px',
    width: '30px',
    marginRight: '8px',
  },

  avatar_profile_text: {
    fontSize: theme.typography.h6.fontSize,
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.accents.main,
  },

  close_container: {
    width: '10%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },

  close_button: { margin: theme.spacing(0.5), padding: '3px' },

  profile_container: {
    margin: '20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '20px',
    height: '100%',
  },

  icon: {
    color: theme.palette.texts.secondary,
    marginRight: '10px',
  },

  aliases_container: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    alignItems: 'center',
  },

  aliases_chips: {
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },

  listHeader: {
    fontSize: theme.fonts.responsiveMedium,
    fontWeight: 'bold',
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.texts.primary,
  },

  info_container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    margin: '8px 2px',
  },

  user_info_container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    border: '1px solid gray',
    padding: '6px',
    borderRadius: '5px',
  },

  user_info_title: {
    fontSize: theme.fonts.responsiveMedium,
    letterSpacing: '0.1rem',
    textTransform: 'uppercase',
    color: theme.palette.texts.primary,
    width: '50%',
    fontWeight: 'bold',
  },

  user_info_text: {
    fontSize: theme.fonts.responsiveMediumSmall,
    letterSpacing: '0.1rem',
    color: theme.palette.texts.primary,
    width: '50%',
  },

  language_header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: '100%',
    margin: '8px 2px',
    wrap: 'nowrap',
  },
}));

export default function DrawerAvatar(props) {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();

  const globalSettings = useSelector((store) => store.global_settings ?? {});

  const userAliases = localStorage.getItem('aliases') ?? null;
  const username = localStorage.getItem('username') ?? null;
  const annotationPermission = localStorage.getItem('annotation') ?? null;
  const diagnosticsPermission = localStorage.getItem('diagnostics') ?? null;
  const lastLogin = localStorage.getItem('last_login') ?? null;
  const role = localStorage.getItem('role') ?? null;
  const [statisticsOptOut, setStatisticsOptOut] = useState(false);

  const { client_id, client, language } = globalSettings;

  const lngs = {
    en: { nativeName: 'English', disable: false },
    fr: { nativeName: 'Français', disable: false },
    de: { nativeName: 'Deutsch', disable: false },
    es: { nativeName: 'Español', disable: false },
    pl: { nativeName: 'Polski', disable: false },
    lt: { nativeName: 'Lithuanian (*BETA)', disable: false },
    it: { nativeName: 'Italiano (*BETA)', disable: false },
  };

  const handleChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  function getInitials(string) {
    if (!string) {
      history.push('/logout');
      return;
    }
    const tokens = string.trim().split(' ');
    return tokens.map((token) => token[0].toUpperCase()).join('');
  }

  const updateLanguage = async (event) => {
    const { value } = event.target;
    const updateBody = {
      language: value,
    };
    try {
      const response = await fetchPut(`user/${username}`, updateBody);
      if (response.success) {
        dispatch(setLanguage(value));
        i18n.changeLanguage(value);
      } else {
        dispatch(
          setSnackbar({
            msg: 'api_messages.snack_user_failed',
            severity: 'error',
          }),
        );
      }
    } catch (error) {
      dispatch(
        setSnackbar({
          msg: 'api_messages.snack_user_failed',
          severity: 'error',
        }),
      );
    }
  };

  const fetchUserStatisticsOptOut = useCallback(async () => {
    if (!username) return;
    const response = await fetchGet(`user/${username}`);
    if (response.success) {
      setStatisticsOptOut(!!response.data.statistics_opt_out);
    }
  }, [username]);

  useEffect(() => {
    if (props.open) {
      fetchUserStatisticsOptOut();
    }
  }, [props.open, fetchUserStatisticsOptOut]);

  const updateStatisticsOptOut = async (event) => {
    const checked = event.target.checked;
    const response = await fetchPut(`user/${username}`, { statistics_opt_out: checked });
    if (response.success) {
      setStatisticsOptOut(checked);
      dispatch(
        setSnackbar({
          msg: 'UserPanel.snack_user_updated',
          severity: 'success',
        }),
      );
      return;
    }

    dispatch(
      setSnackbar({
        msg: 'UserPanel.snack_user_failed',
        severity: 'error',
      }),
    );
  };

  return (
    <Drawer
      anchor={'right'}
      transitionDuration={600}
      open={props.open}
      onClose={(_e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          props.onClose();
        }
        props.onClose();
      }}
      classes={{
        paper: classes.drawerAvatarPaper,
      }}
      data-test-id='drawer-avatar'>
      <div className={classes.drawerHeader}>
        <div className={classes.close_container}>
          <Tooltip
            arrow
            placement='right'
            title={<p className={classes.tooltip}>{t('Console.close')}</p>}
            aria-label='add'>
            <IconButton
              edge='start'
              className={classes.close_button}
              color='inherit'
              aria-label='menu'
              onClick={() => props.onClose()}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </div>
        <div className={classes.avatar_container}>
          <Avatar className={classes.profile_avatar}>{getInitials(localStorage.getItem('username'))}</Avatar>
          <Typography className={classes.avatar_profile_text}>
            {localStorage.getItem('username')?.toUpperCase()}
          </Typography>
        </div>
      </div>
      <Divider />

      {/* user info */}
      <div className={classes.profile_container}>
        {/* Dicom operator names */}
        {userAliases && userAliases !== 'undefined' && (
          <div className={classes.aliases_container}>
            <div className={classes.aliases_chips}>
              <AccountBoxIcon className={classes.icon} fontSize='small' />
              <Typography className={classes.listHeader}>{t('users.dicomTag')}</Typography>
            </div>
            <div>
              {userAliases?.split(';').map((alias, i) => (
                <Chip
                  key={i}
                  style={{ margin: '6px' }}
                  size='small'
                  label={alias.trim() ? alias.trim() : '-'}
                />
              ))}
            </div>
          </div>
        )}

        {/* User language */}
        <div className={classes.language_container}>
          <div className={classes.language_header}>
            <LanguageIcon className={classes.icon} fontSize='small' />
            <Typography className={classes.listHeader}>{t('Menu.language')}</Typography>
          </div>

          <div style={{ paddingTop: '0px' }}>
            <RadioGroup
              name='language'
              value={language}
              onChange={(event) => {
                updateLanguage(event);
              }}
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
              {Object.keys(lngs).map((lng) => (
                <FormControlLabel
                  key={lng}
                  value={lng}
                  control={<Radio />}
                  label={lngs[lng].nativeName}
                  disabled={lngs[lng].disable}
                  style={{
                    width: '45%',
                  }}
                />
              ))}
            </RadioGroup>
          </div>
        </div>

        <div className={classes.language_container}>
          <div className={classes.language_header}>
            <Typography className={classes.listHeader}>{t('UserPanel.statistics_opt_out')}</Typography>
          </div>
          <FormControlLabel
            control={<Checkbox checked={statisticsOptOut} onChange={updateStatisticsOptOut} color='primary' />}
            label={t('UserPanel.statistics_opt_out_helper')}
          />
        </div>

        {/* Password */}
        <ChangePassword />

        {/* user general info */}
        <div className={classes.user_info_container}>
          <Typography className={classes.avatar_profile_text}>{t('UserPanel.user_info')}</Typography>
          <div className={classes.info_container}>
            <Typography className={classes.user_info_title}>{t('LoginRegister.username')}:</Typography>
            <Typography className={classes.user_info_text}>{localStorage.getItem('username')}</Typography>
          </div>
          <div className={classes.info_container}>
            <Typography className={classes.user_info_title}>{t('LoginRegister.role')}:</Typography>
            <Typography className={classes.user_info_text}>{role ? role : '-'}</Typography>
          </div>
          <div className={classes.info_container}>
            <Typography className={classes.user_info_title}>DANAI:</Typography>
            <Typography className={classes.user_info_text}>
              {annotationPermission === 'true' ? t('UserPanel.annotating') : t('UserPanel.no_annotating')}
            </Typography>
          </div>
          <div className={classes.info_container}>
            <Typography className={classes.user_info_title}>{t('UserPanel.b_diagnostics')}:</Typography>
            <Typography className={classes.user_info_text}>
              {diagnosticsPermission === 'true' ? t('UserPanel.enable') : t('UserPanel.disable')}
            </Typography>
          </div>
          <div className={classes.info_container}>
            <Typography className={classes.user_info_title}>{t('users.lastlogin')}:</Typography>
            <Typography className={classes.user_info_text}>{lastLogin ? lastLogin : '-'}</Typography>
          </div>
        </div>

        {/* client info */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            margin: '10px',
            borderTop: '1px solid #808080',
            alignItems: 'center',
          }}>
          <Typography style={{ margin: '8px', fontStyle: 'italic', color: '#808080', fontSize: '11px' }}>
            {client} - {client_id}
          </Typography>
        </div>
      </div>
    </Drawer>
  );
}
